import {
  TextV2 as Text,
  Box,
  Flex,
  Form,
  Loader,
  Colors,
  Tag,
  Tooltip,
} from '@pinchenterprisesnpm/friday-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRosterMembersData } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/roster-section/hooks/useRosterData'
import { ACCEPTANCE_CRITERIA_OPTION } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/acceptance-criteria-step.types'
import { connect } from 'formik'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import { useMemo } from 'react'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import { ROSTER_INVITE_STATUS } from '@src/models/Roster'
import { UserListItem } from '../user-list-item'
import { IRosterSectionProps } from './roster-section.types'
import { DragHandleWrapper, StyledRosterSection } from './roster-section.styles'

const { Checkbox } = Form

export const RosterSection = connect<IRosterSectionProps>(
  ({
    roster,
    isExpanded,
    onToggle,
    customOnSelect,
    customIsSelected,
    businessId,
    dragHandle,
    formik = {
      values: {
        roster_ids: [],
        auto_accept_by_rosters: false,
        auto_accept: false,
      },
    },
    containerStyle,
    invite_status,
    isUnavailable,
  }) => {
    const { values, setFieldValue } = formik

    const { isFetching, members } = useRosterMembersData(roster.id, isExpanded, businessId)
    const isSelected =
      customIsSelected ?? ((values?.roster_ids ?? []) as number[]).includes(roster.id) ?? false

    const selectedAcceptanceCriteriaOption = useMemo(() => {
      return getSelectedAcceptanceCriteriaOption(
        values?.auto_accept_by_rosters,
        values?.auto_accept
      )
    }, [values?.auto_accept_by_rosters, values?.auto_accept])

    const isACVerifiedFromRosters =
      selectedAcceptanceCriteriaOption === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS
    const isSelectionDisabled = isACVerifiedFromRosters || isUnavailable

    const badgeProps = useMemo(() => {
      if (invite_status === undefined) {
        return null // Don't show badge if invite_status is undefined
      }

      if (invite_status === ROSTER_INVITE_STATUS.FINISHED) {
        return {
          text: 'Invited Before',
          type: 'warning_300' as const,
        }
      } else if (invite_status === ROSTER_INVITE_STATUS.PENDING) {
        return {
          text: 'Invitation in Progress',
          type: 'primary' as const,
        }
      }
      return null
    }, [invite_status])

    const onSelect =
      customOnSelect ??
      ((id: number) => {
        if (isSelected) {
          setFieldValue?.(
            'roster_ids',
            values?.roster_ids?.filter((rosterId) => rosterId !== id) ?? []
          )
        } else {
          setFieldValue?.('roster_ids', [...(values?.roster_ids ?? []), id])
        }
      })

    return (
      <StyledRosterSection style={containerStyle} isSelectionDisabled={isSelectionDisabled}>
        <Box
          backgroundColor='primary.50'
          p='sm'
          onClick={() => onToggle(roster.id)}
          style={{ cursor: 'pointer', position: 'relative' }}
        >
          <Flex justifyContent='space-between' alignItems='center' width='100%'>
            <Flex direction='column' width='100%'>
              <Flex justifyContent='space-between' alignItems='center' width='100%'>
                <Flex alignItems='center'>
                  <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} ml='xs' mt='xs'>
                    <Checkbox
                      isDisabled={isSelectionDisabled}
                      value={isSelected}
                      onChange={() => onSelect(roster.id)}
                      borderRadius='4px'
                      borderWidth='2px'
                      borderColor={Colors.muted}
                      backgroundColor={Colors.white}
                    />
                  </Box>
                  <Text variant='h6'>{roster.name}</Text>
                </Flex>
              </Flex>
              <Box mt='xs' ml='lg'>
                <Text color='gray' ml='lg'>
                  {roster.members_count} total
                </Text>
              </Box>
            </Flex>
            <Flex mr='md'>
              {isUnavailable && (
                <Tooltip text='None of the Upshifters in this roster are eligible for an invite.'>
                  <span>
                    <Tag size='small' type='danger' mx='xs'>
                      Unavailable
                    </Tag>
                  </span>
                </Tooltip>
              )}
              {badgeProps && (
                <Box style={{ whiteSpace: 'nowrap', marginRight: '8px' }} ml='xs'>
                  <Tag type={badgeProps.type}>{badgeProps.text}</Tag>
                </Box>
              )}
              <DragHandleWrapper
                mx='md'
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                isSelectionDisabled={isSelectionDisabled}
              >
                {dragHandle}
              </DragHandleWrapper>
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                color='gray'
                size='lg'
              />
            </Flex>
          </Flex>
        </Box>

        {isFetching && (
          <Flex justifyContent='center' alignItems='center'>
            <Loader />
          </Flex>
        )}

        {isExpanded && members && !isFetching && (
          <Box ml='md'>
            {members.map((user) => (
              <UserListItem key={user.id} user={user} />
            ))}
          </Box>
        )}
      </StyledRosterSection>
    )
  }
)
